// What can be moved off a Mission Panel card, as data (#6771, epic #6552; prototype itemInfo
// `:1570`, canGrip `:2139`, the epic grip `:2365`): the three item shapes the move doors take
// (`MoveItemWireSchema`, #6769) with the words every renderer names them by, and whether each
// shows a grip.
//
// A GRIP FOLLOWS STATE AND THE BLOCKER JOIN ONLY (owner ruling 2026-10-20: card state follows
// the real machine and its jobs). A job's grip needs the machine online, the job not
// `starting`, `pausing` or `last_known`, and no Parked blocker on its task (waiting on a
// person; the card's join by `task_id`, `./machine-card.ts`). An epic's grip also needs none
// of its jobs on this card waiting on a person. A group's grip needs at least one grippable
// job AND every job under the header sharing ONE non-null `mode_key`: the header groups by
// `capabilityKey(role)` (`./machine-jobs.ts` `jobGroups`) and `mode_key` differs from `role`
// for `pr_babysit` and others, so a mixed header gets no group grip and its jobs move one by
// one. Moving and resting are not on the job row (#6768), so they never disable a grip: the
// preview's source refusal refuses them.
//
// Nothing here computes a limit. Whether a move may happen is the server's preview.

import type { AgentModeKey } from '@developerz/domain';
import type { Translator } from '@developerz/i18n';
import type { ApiBlocker } from './blockers';
import { OFFLINE_STATUSES } from './fleet-machines';
import type { UtilBox, UtilJob } from './fleetUtilization';
import { BLOCKER_TAG } from './machine-card';
import { capabilityKey, jobState, jobTitle } from './machine-jobs';
import type { MoveItem } from './move-calls';

/** One thing a person can move, with every word its renderers name it by. */
export type MoveSubject = {
  readonly item: MoveItem;
  /** The machine it moves off. */
  readonly sourceId: string;
  /** Page-unique: the machine and the item. */
  readonly key: string;
  /** "Coder job", "Epic <title>", "Coder jobs": the grip's and the menu's label. */
  readonly label: string;
  /** The ghost's first line: "Coder", "Epic <title>", "Coder group". */
  readonly top: string;
  /** The ghost's badge: "N jobs" for an epic, "Epic <title>" for a job in one; else null. */
  readonly badge: string | null;
  /** The job's title, the epic's title, or "N jobs" for a group. */
  readonly title: string;
  /** "The Coder job", "Epic <title> and its N jobs", "The Coder jobs": who moves. */
  readonly what: string;
  /** The verb after `what` is plural ("are moving"). */
  readonly many: boolean;
  /** The jobs on the card it names. */
  readonly jobs: readonly UtilJob[];
};

const subjectKey = (sourceId: string, item: MoveItem): string =>
  `${sourceId}|${item.kind}:${item.id}`;

/** A job. */
export function jobSubject(box: UtilBox, job: UtilJob, tr: Translator): MoveSubject {
  const role = tr(capabilityKey(job.role));
  const item: MoveItem = { kind: 'task', id: job.id };
  return {
    item,
    sourceId: box.id,
    key: subjectKey(box.id, item),
    label: tr('fleet.move.label_job', { role }),
    top: role,
    badge: job.epic == null ? null : tr('fleet.move.label_epic', { title: job.epic.title }),
    title: jobTitle(job, tr),
    what: tr('fleet.move.what_job', { role }),
    many: false,
    jobs: [job],
  };
}

/** An epic, by its plan id and title, with its jobs on this card. */
export function epicSubject(
  box: UtilBox,
  epic: { readonly plan_id: string; readonly title: string },
  tr: Translator,
): MoveSubject {
  const jobs = (box.jobs ?? []).filter((j) => j.epic?.plan_id === epic.plan_id);
  const n = jobs.length;
  const item: MoveItem = { kind: 'epic', id: epic.plan_id };
  return {
    item,
    sourceId: box.id,
    key: subjectKey(box.id, item),
    label: tr('fleet.move.label_epic', { title: epic.title }),
    top: tr('fleet.move.label_epic', { title: epic.title }),
    badge: tr.plural('fleet.machines.card_jobs', n, { count: n }),
    title: epic.title,
    what:
      n === 0
        ? tr('fleet.move.what_epic', { title: epic.title })
        : tr.plural('fleet.move.what_epic_jobs', n, { title: epic.title, count: n }),
    many: n > 0,
    jobs,
  };
}

/** A capability group, by the header's shared mode key. */
export function groupSubject(
  box: UtilBox,
  modeKey: AgentModeKey,
  jobs: readonly UtilJob[],
  tr: Translator,
): MoveSubject {
  const role = tr(capabilityKey(jobs[0]?.role ?? null));
  const item: MoveItem = { kind: 'group', id: modeKey };
  return {
    item,
    sourceId: box.id,
    key: subjectKey(box.id, item),
    label: tr('fleet.move.label_group', { role }),
    top: tr('fleet.move.ghost_group', { role }),
    badge: null,
    title: tr.plural('fleet.machines.card_jobs', jobs.length, { count: jobs.length }),
    what: tr('fleet.move.what_group', { role }),
    many: true,
    jobs,
  };
}

/** The machine is checking in. */
export const machineOnline = (box: Pick<UtilBox, 'verdict'>): boolean =>
  !OFFLINE_STATUSES.includes(box.verdict.status);

/** An open Parked blocker on the task: it waits on a person's answer. */
export function waitingOnPerson(
  taskId: string,
  byTask: ReadonlyMap<string, readonly ApiBlocker[]>,
): boolean {
  return (byTask.get(taskId) ?? []).some((b) => BLOCKER_TAG[b.kind] === 'parked');
}

const UNGRIPPABLE = new Set(['starting', 'pausing', 'last_known']);

/** A job's grip shows: the machine online, the job past starting and not waiting on a person. */
export function jobGrippable(
  box: Pick<UtilBox, 'verdict'>,
  job: UtilJob,
  byTask: ReadonlyMap<string, readonly ApiBlocker[]>,
): boolean {
  const s = jobState(job);
  return machineOnline(box) && !UNGRIPPABLE.has(s ?? '') && !waitingOnPerson(job.id, byTask);
}

/** An epic's grip shows: the machine online and none of its jobs here waiting on a person. */
export function epicGrippable(
  box: UtilBox,
  planId: string,
  byTask: ReadonlyMap<string, readonly ApiBlocker[]>,
): boolean {
  if (!machineOnline(box)) return false;
  return !(box.jobs ?? []).some((j) => j.epic?.plan_id === planId && waitingOnPerson(j.id, byTask));
}

/**
 * The header's shared mode key when its group may be gripped: at least one grippable job and
 * every job under it on one non-null `mode_key`; else null (no group grip).
 */
export function groupModeKey(
  box: Pick<UtilBox, 'verdict'>,
  jobs: readonly UtilJob[],
  byTask: ReadonlyMap<string, readonly ApiBlocker[]>,
): AgentModeKey | null {
  const first = jobs[0]?.mode_key ?? null;
  if (first === null) return null;
  if (!jobs.every((j) => j.mode_key === first)) return null;
  return jobs.some((j) => jobGrippable(box, j, byTask)) ? first : null;
}

/**
 * Why a grip on this machine is disabled, translated, or null: the machine's own move budget
 * read 0 (#6768's `moves.left`), or the page's flood guard is locked.
 */
export function gripDisabledWhy(
  box: Pick<UtilBox, 'moves'>,
  lockedSeconds: number,
  tr: Translator,
): string | null {
  if (lockedSeconds > 0) return tr('fleet.move.grip_why_locked');
  if (box.moves != null && box.moves.left <= 0) return tr('fleet.move.grip_why_limit');
  return null;
}
